import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { FileDropzone } from '../../../../../components/ui/FileDropzone';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { uploadPutaway } from '../../../../../features/putaway/putawayThunks';
import {
  PUTAWAY_TEMPLATE_COLUMNS,
  PUTAWAY_TEMPLATE_EXAMPLE,
  bulkDoneNotice,
} from '../../../../../features/putaway/putawayUtils';

/** The server reads the sheet with openpyxl, which does not open the old `.xls`. */
const SHEET_EXTENSIONS = ['.xlsx'];

/** How many of the sheet's problems are listed before "y N más". */
const PROBLEMS_SHOWN = 12;

const downloadTemplate = () => {
  const sheet = XLSX.utils.json_to_sheet(PUTAWAY_TEMPLATE_EXAMPLE, { header: PUTAWAY_TEMPLATE_COLUMNS });
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Almacenado');
  XLSX.writeFile(book, 'plantilla-almacenado.xlsx');
};

/**
 * Puts away from a sheet, for ONE client in the warehouse in use.
 *
 * Somebody already decided in an Excel where each thing goes — after a count,
 * or in a big first load — and ticking forty rows on the screen makes no
 * sense. One row per SKU with the quantity and the destination, the lot only
 * when the SKU sits on the dock in more than one. Each row is resolved against
 * what that client has in receiving, and it is all or nothing: the server
 * names every problem with its row in the Excel, so the sheet goes back once.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.warehouse - The warehouse in use.
 * @param {Array<{value: number, label: string}>} props.clientOptions
 * @param {(notice: {type: string, message: string}) => void} props.onDone
 */
export const PutawayUploadModal = ({ isOpen, onClose, warehouse, clientOptions, onDone }) => {
  const dispatch = useDispatch();
  const uploading = useSelector((state) => state.putaway.uploading);

  const [companyId, setCompanyId] = useState(null);
  const [file, setFile] = useState(null);
  const [failure, setFailure] = useState(null);

  const onlyClientId = clientOptions.length === 1 ? clientOptions[0].value : null;

  // Every opening starts without a file.
  useEffect(() => {
    if (!isOpen) return;
    setCompanyId(onlyClientId);
    setFile(null);
    setFailure(null);
  }, [isOpen, onlyClientId]);

  const problem = (() => {
    if (!companyId) return 'Elige el cliente';
    if (!file) return 'Elige la planilla';
    return null;
  })();

  const handleUpload = async () => {
    setFailure(null);
    const action = await dispatch(uploadPutaway({ warehouseId: warehouse.id, companyId, file }));

    if (action.meta.requestStatus !== 'fulfilled') {
      // Nothing went in. The sheet stays staged so it is clear which one failed.
      setFailure(action.payload);
      return;
    }

    onDone(bulkDoneNotice(action.payload, 'la ubicación de cada fila'));
    onClose();
  };

  const problems = failure?.errors || [];
  const hidden = Math.max(problems.length - PROBLEMS_SHOWN, 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Carga masiva de almacenado"
      testId="putaway-upload-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {warehouse?.name} · una fila por SKU, con su cantidad y la ubicación destino
      </p>

      <div className="space-y-4">
        {failure && (
          <div
            data-testid="putaway-upload-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm space-y-2"
          >
            <p>{failure.message}</p>
            {problems.length > 0 && (
              <ul className="list-disc pl-5 space-y-0.5 text-xs">
                {problems.slice(0, PROBLEMS_SHOWN).map((item, index) => (
                  <li key={`${item.row}-${index}`} data-testid="putaway-upload-problem">
                    {item.row ? `Fila ${item.row}: ` : ''}
                    {item.message}
                  </li>
                ))}
                {hidden > 0 && <li>y {hidden} más</li>}
              </ul>
            )}
          </div>
        )}

        {clientOptions.length > 1 && (
          <div>
            <FilterableSelect
              variant="form"
              testId="putaway-upload-client"
              label="Cliente"
              value={companyId}
              onChange={setCompanyId}
              options={clientOptions}
              placeholder="Seleccionar..."
              searchPlaceholder="Buscar cliente..."
              disabled={uploading}
            />
            <p className="text-text-muted text-xs mt-1">
              Todos los SKU de la planilla tienen que ser de esta empresa.
            </p>
          </div>
        )}

        <FileDropzone
          file={file}
          onFileSelected={(selected) => {
            setFile(selected);
            setFailure(null);
          }}
          onClear={() => setFile(null)}
          extensions={SHEET_EXTENSIONS}
          hint="Planilla .xlsx con las columnas SKU, Cantidad y Ubicación"
          disabled={uploading}
        />

        <div className="text-xs text-text-muted space-y-1">
          <p>
            Columnas: <strong>SKU</strong>, <strong>Cantidad</strong> y <strong>Ubicación</strong>{' '}
            (nombre o código, de Almacenaje o Picking) obligatorias. <strong>Lote</strong> solo
            cuando el SKU está en recepción en más de un lote.
          </p>
          <p>
            Cada fila sale de lo que ese cliente tiene en recepción de esta bodega. Es todo o nada:
            si una fila falla, no se guarda ninguna, y se te dicen todas las filas con problemas de
            una vez.
          </p>
        </div>

        <Button
          variant="ghost"
          size="sm"
          icon={Download}
          onClick={downloadTemplate}
          data-testid="putaway-upload-template"
        >
          Descargar plantilla
        </Button>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={uploading}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={uploading}
          loadingLabel="Cargando..."
          disabled={Boolean(problem)}
          onClick={handleUpload}
          data-testid="putaway-upload-submit"
        >
          {problem || 'Cargar planilla'}
        </Button>
      </div>
    </Modal>
  );
};
